import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import axios from "axios";
import { Tree, TreeNode as OrgTreeNode } from "react-organizational-chart";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";

/* ---------------- DUMMY DATA ---------------- */
const projectData = {
  type: "Residential Project",
  name: "Arc Tower",
  developer: "Ram Realty",
  address: "Pune, Maharashtra",
  contact: "+918530844548",
  components: [
    {
      title: "Parking",
      details: "Details added about parking",
      subItems: [
        {
          title: "Basement Parking",
          details: "Details added about Basement Parking",
          subItems: [
            { title: "B1", details: "Details added about B1" },
            { title: "B2", details: "Details added about B2" },
          ],
        },
        {
          title: "Podium Parking",
          details: "Details added about Podium Parking",
        },
      ],
    },
    {
      title: "Floors",
      details: "Details about Floor like total Floors and all",
    },
  ],
};

/* ---------------- COLLAPSIBLE TREE NODE ---------------- */
const CollapsibleTreeNode = ({ node, level = 0, allMasterMappings = [] }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const children = node.subItems || node.children || node.components || [];
  const hasSubItems = Array.isArray(children) && children.length > 0;

  // Find master node for labels
  let masterNode = null;
  const findInTemplate = (items) => {
    for (const item of items) {
      if (item.title === node.title) {
        masterNode = item;
        return true;
      }
      if (item.subItems && findInTemplate(item.subItems)) return true;
    }
    return false;
  };

  if (level > 0) {
    for (const mapping of allMasterMappings) {
      if (findInTemplate(mapping.components || [])) break;
    }
  }

  // Determine which details to show. 
  // If the node has its own extraDetails, use those (they include custom ones).
  // Otherwise, fallback to master template keys.
  const displayDetails = (Array.isArray(node.extraDetails) && node.extraDetails.length > 0)
    ? node.extraDetails
    : (masterNode?.extraDetails || []);

  const cardStyle = {
    backgroundColor: "#fff",
    color: COLORS.SECONDARY,
    borderRadius: "5px",
    px: 3,
    py: 1.5,
    minWidth: "200px",
    textAlign: "center",
    boxShadow: "0px 2px 10px rgba(0,0,0,0.06)",
    border: `1px solid ${COLORS.BORDER}`,
    position: "relative",
    display: "inline-block",
    zIndex: 2,
  };

  const iconButtonStyle = {
    position: "absolute",
    bottom: "-12px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#fff",
    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 10,
    boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
    "&:hover": { backgroundColor: "#f8f9fa" },
  };

  const label = (
    <Box sx={cardStyle}>
      <Typography sx={{ fontWeight: 700, fontSize: "14px", mb: 0.5 }}>
        {node.title}
      </Typography>

      {(node.details || displayDetails.length > 0) && (
        <Box sx={{ borderTop: `1px solid ${COLORS.BORDER}`, pt: 0.5, mt: 0.5 }}>
          {node.details && (
            <Typography
              sx={{ fontSize: "11px", color: COLORS.TEXT, opacity: 0.8 }}
            >
              {node.details}
            </Typography>
          )}
          {displayDetails.map((d, i) => {
            const keyLabel = typeof d === "string" ? d : d.key;
            let displayValue = "—";

            if (typeof d === "object" && d.value !== undefined) {
              displayValue = d.value;
            } else if (Array.isArray(node.extraDetails)) {
              const found = node.extraDetails.find((ed) => ed.key === keyLabel);
              if (found) displayValue = found.value;
            }

            return (
              <Typography
                key={i}
                sx={{
                  fontSize: "11px",
                  color: COLORS.PRIMARY,
                  fontWeight: 500,
                }}
              >
                {keyLabel}: {displayValue}
              </Typography>
            );
          })}
        </Box>
      )}

      {hasSubItems && (
        <Box onClick={() => setIsExpanded(!isExpanded)} sx={iconButtonStyle}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: 1,
              color: COLORS.PRIMARY,
            }}
          >
            {isExpanded ? "−" : "+"}
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (level === 0) {
    return (
      <Tree
        lineWidth={"2px"}
        lineColor={COLORS.LIGHT_SECONDARY}
        lineBorderRadius={"5px"}
        label={label}
      >
        {isExpanded &&
          children.map((child, idx) => (
            <CollapsibleTreeNode
              key={idx}
              node={child}
              level={level + 1}
              allMasterMappings={allMasterMappings}
            />
          ))}
      </Tree>
    );
  }

  return (
    <OrgTreeNode label={label}>
      {isExpanded &&
        children.map((child, idx) => (
          <CollapsibleTreeNode
            key={idx}
            node={child}
            level={level + 1}
            allMasterMappings={allMasterMappings}
          />
        ))}
    </OrgTreeNode>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */
const ViewMapping = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth?.() || {};
  const [project, setProject] = useState(null);
  const [allMasterMappings, setAllMasterMappings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) fetchProjectDetails();
    else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const pRes = await axios.get(`${url}/api/projects/get/${projectId}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      if (pRes.data?.success) setProject(pRes.data.data);

      const mRes = await axios.get(
        `${url}/api/project-mappings/get/${projectId}`,
        {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        },
      );

      if (mRes.data?.success && mRes.data.data?.length > 0) {
        const mappings = mRes.data.data;
        
        let allTowers = [];
        let allSourceTemplates = new Set();
        let allProjectTypes = new Set();

        mappings.forEach((mapping) => {
          if (mapping.sourceTemplates) {
            mapping.sourceTemplates.forEach(t => allSourceTemplates.add(t));
          } else if (mapping.projectType) {
            mapping.projectType.split("+").map(t => t.trim()).forEach(t => allProjectTypes.add(t));
          }
           
          allTowers.push({
            title: mapping.towerName || "Main Mapping",
            details: mapping.projectType || "",
            subItems: mapping.components || []
          });
        });

        // If there's only 1 mapping and it doesn't have a tower name, just attach components directly
        let finalComponents = [];
        if (mappings.length === 1 && !mappings[0].towerName) {
           finalComponents = mappings[0].components || [];
        } else {
           finalComponents = allTowers;
        }

        setProject((prev) => ({
          ...prev,
          components: finalComponents,
          projectType: Array.from(allProjectTypes).join(" + "),
        }));

        const pTypes = allSourceTemplates.size > 0 
           ? Array.from(allSourceTemplates) 
           : Array.from(allProjectTypes);

        if (pTypes.length > 0) {
          const mPromises = pTypes.map((t) =>
            axios
              .get(`${url}/api/master-mappings/get-by-name/${t}`, {
                headers: {
                  Authorization: token ? `Bearer ${token}` : undefined,
                },
              })
              .catch(() => null),
          );
          const mResponses = await Promise.all(mPromises);
          setAllMasterMappings(
            mResponses
              .filter((r) => r && r.data?.success)
              .map((r) => r.data.data),
          );
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const displayProject = project || projectData;

  const rootNode = {
    title: displayProject.projectName || displayProject.name,
    details: [
      displayProject.developer || displayProject.clientName || "-",
      displayProject.projectType || displayProject.type
    ].filter(Boolean).join(" • "),
    subItems: displayProject.components || [],
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* FIXED HEADER */}
      <Box
        sx={{
          p: 2,
          backgroundColor: "#fff",
          borderBottom: `1px solid ${COLORS.BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 1000,
          boxShadow: "0px 2px 10px rgba(0,0,0,0.02)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: COLORS.PRIMARY }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{ ...TYPOGRAPHY.H4, fontWeight: 700, color: COLORS.SECONDARY }}
          >
            Project Mapping Hierarchy
          </Typography>
        </Box>

        {/* ZOOM CONTROLS - Will be injected via TransformWrapper reference elsewhere if needed, 
                    but here we can just use the internal context if we wrap the whole body. 
                    Actually, we'll put the wrapper around the workspace and use the render prop for controls. */}
        <Box id="zoom-controls-placeholder" />

        <Box
          sx={{
            px: 3,
            py: 1,
            borderRadius: "30px",
            backgroundColor: COLORS.LIGHT_SECONDARY,
            color: COLORS.PRIMARY,
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          INTERACTIVE VIEW
        </Box>
      </Box>

      {/* SCROLLABLE WORKSPACE WITH ZOOM & PAN */}
      <TransformWrapper
        initialScale={1}
        initialPositionX={0}
        initialPositionY={0}
        centerOnInit={true}
        minScale={0.2}
        maxScale={3}
        limitToBounds={false}
      >
        {({ zoomIn, zoomOut, resetTransform, centerView }) => (
          <React.Fragment>
            {/* Control Toolbar (Floating/Overlay) */}
            <Box
              sx={{
                position: "fixed",
                bottom: 30,
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(5px)",
                borderRadius: "50px",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
                display: "flex",
                gap: 1,
                p: 1,
                zIndex: 2000,
                border: `1px solid ${COLORS.BORDER}`,
              }}
            >
              <IconButton
                onClick={() => zoomIn()}
                sx={{ color: COLORS.PRIMARY }}
                title="Zoom In"
              >
                <ZoomInIcon />
              </IconButton>
              <IconButton
                onClick={() => zoomOut()}
                sx={{ color: COLORS.PRIMARY }}
                title="Zoom Out"
              >
                <ZoomOutIcon />
              </IconButton>
              <IconButton
                onClick={() => centerView()}
                sx={{ color: COLORS.SECONDARY }}
                title="Center View"
              >
                <CenterFocusStrongIcon />
              </IconButton>
              <IconButton
                onClick={() => resetTransform()}
                sx={{ color: COLORS.TEXT }}
                title="Reset Zoom"
              >
                <RestartAltIcon />
              </IconButton>
            </Box>

            <TransformComponent
              wrapperStyle={{
                width: "100%",
                height: "100%",
                backgroundColor: "#FDFDFD",
                backgroundImage:
                  "radial-gradient(#e5e7eb 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
              contentStyle={{
                width: "max-content",
                height: "max-content",
                minWidth: "100%",
                minHeight: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "100px", // Provide space for large trees
              }}
            >
              <Box sx={{ mt: 4, mb: 10 }}>
                <CollapsibleTreeNode
                  node={rootNode}
                  level={0}
                  allMasterMappings={allMasterMappings}
                />
              </Box>
            </TransformComponent>
          </React.Fragment>
        )}
      </TransformWrapper>
    </Box>
  );
};

export default ViewMapping;
